import { useContext, useCallback } from 'react';
import { useForm, Resolver, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import yupResolver from '@wix/da-shared-react/pkg/utils/yupResolver';
import { getMaxPriceForCoreLevel } from '@wix/da-shared-react/pkg/utils/profits';
import { ExclusiveCategory } from '@wix/da-shared-react/pkg/types/exclusives';
import {
  FormField,
  CreateAdoptableFormValues,
  EditAdoptableFormValues,
} from '../redux/types';
import { exclusiveCategoriesFormData } from '../constants';
import type { PapiDeviation, PapiAttachmentFile } from '@wix/da-papi-types';
import {
  getSourceUrlInputName,
  getSourceFilesInputName
} from '../redux/helpers';
import { useSelector } from 'react-redux';
import { getExtraDeviationData } from '../redux/sellerInitialization/selectors';

export const DEFAULT_VALUES: CreateAdoptableFormValues = {
  [FormField.SellAsFixed]: true,
  [FormField.PurchasePrice]: '15',
  [FormField.SellAsOffer]: true,
  [FormField.AllowResale]: true,
  [FormField.EarlyAccess]: false,
  [FormField.Offer]: '5',
  [FormField.Duration]: '2',
  [FormField.OfferDelayedStart]: true,
  [FormField.AcceptTerms]: true,
  [FormField.IncludeBatchSources]: true,
  [FormField.Category]:
    exclusiveCategoriesFormData[ExclusiveCategory.Adoptable].necoId,
};

// Lifted gently from the PCP init adapter
export const assetAdapter = asset => ({
  name: asset.filename,
  size: asset.filesize,
  type: asset.type,
  deviationId: asset.isOriginal ? 0 : asset.deviationId,
  fileId: asset.fileId,
});

const jsonArrayHasAtLeastOne = (str: string) => {
  try {
    const parsed = JSON.parse(str);
    return (Array.isArray(parsed) && parsed.length >= 1);
  } catch (e) {
    return false;
  }
};

export function useCreateForm(
  deviations: PapiDeviation[],
  isRelisting: boolean,
  initialAssets?: {[deviationId: number]: PapiAttachmentFile[]}
) {
  const { t } = useTranslation();
  const session = useContext(SessionContext);
  const extraDeviationData = useSelector(getExtraDeviationData);
  const maxPriceForCoreLevel = getMaxPriceForCoreLevel(session.coreLevel);

  return useForm<{
    // sources are handled specially since there can be multiple of these inputs in batch settings
    [key in Exclude<FormField, 'source_url' | 'source_files'>]: any;
  }>({
    // since we automatically uncheck the sell_as checkboxes when
    // its inputs are cleared, we use onSubmit here instead of onChange
    // or we get bad 'required' errors
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      ...DEFAULT_VALUES,
      ...deviations.reduce(
        (accumulator, deviation) => ({
          ...accumulator,
          [getSourceUrlInputName(deviation)]:
            deviation.extended?.adoptable?.assetSourceUrl ?? '',
          [getSourceFilesInputName(deviation)]:
            JSON.stringify(
              (
                initialAssets?.[deviation.deviationId] ??
                  deviation.extended?.adoptable?.assets ??
                  []
              ).map(assetAdapter)
            ),
        }),
        {}
      ),
    },
    resolver: (async (data: CreateAdoptableFormValues, context) => {
      const requiredMsg = t('sellerModal.error.required');
      const purchasePosIntMsg = t(
        'sellerModal.create.purchase_price.error.posInt'
      );
      const offerPosIntMsg = t('sellerModal.create.offer.error.posInt');
      const maxPriceMsg = t(
        'sellerModal.create.purchase_price.error.maxPrice',
        {
          maxPrice: maxPriceForCoreLevel,
        }
      );
      const moreThanFixedMsg = t(
        'sellerModal.create.offer.error.moreThanFixed'
      );
      const validUrl = t('sellerModal.create.source_url.invalidUrl');

      const purchasePriceValidator = yup.lazy(value => {
        if (value === '') {
          return yup.string();
        }
        return yup
          .number()
          .positive(purchasePosIntMsg)
          .integer(purchasePosIntMsg)
          .max(maxPriceForCoreLevel, maxPriceMsg);
      });
      const offerPriceValidator = yup.lazy(value =>
        value === ''
          ? yup.string()
          : yup.number().positive(offerPosIntMsg).integer(offerPosIntMsg)
      );
      const { values, errors } = await yupResolver<CreateAdoptableFormValues>(
        yup.object({
          [FormField.Category]: yup.string(),
          [FormField.SellAsFixed]: yup.boolean(),
          [FormField.SellAsOffer]: yup.boolean(),
          [FormField.PurchasePrice]: purchasePriceValidator,
          [FormField.AllowResale]: yup.boolean(),
          [FormField.EarlyAccess]: yup.boolean(),
          [FormField.OfferDelayedStart]: yup.boolean(),
          [FormField.IncludeBatchSources]: yup.boolean(),
          [FormField.Offer]: offerPriceValidator,
          [FormField.Duration]: yup.lazy(value =>
            value === '' ? yup.string() : yup.number()
          ),
          [FormField.AcceptTerms]: yup
            .boolean()
            .required(requiredMsg)
            .oneOf([true], requiredMsg),
          ...deviations
            .filter(deviation => {
              // We need to filter to only the ones that support adoptables to avoid requiring a source url input for
              // those. Otherwise, we'd never be able to submit the form.
              // And we'll assume if extraDeviationData doesn't include a matching deviationId, then we're accessing
              // this modal from a non-batch endpoint, so we can require every source input (which should be one).
              const sellableAs =
                deviation.extended?.sellableAs ??
                extraDeviationData?.[deviation.deviationId]?.sellableAs;
              return !sellableAs || sellableAs.includes('adoptable');
            })
            .reduce(
              (accumulator, deviation) => ({
                ...accumulator,
                [getSourceUrlInputName(deviation)]:
                  yup.string().url(validUrl),
                [getSourceFilesInputName(deviation)]: yup
                  .string()
                  .test(
                    'at-least-one',
                    requiredMsg,
                    str => jsonArrayHasAtLeastOne(str)
                  ),
              }),
              {}
            ),
        })
      )(data, context);

      const {
        [FormField.SellAsFixed]: sellAsFixed,
        [FormField.PurchasePrice]: purchasePrice,
        [FormField.SellAsOffer]: sellAsOffer,
        [FormField.Offer]: offer,
        [FormField.Duration]: duration,
      } = data;
      // we make the sell_as fields into two sets that are required inputs
      // if the assoc. checkboxes are true
      if (sellAsFixed && !purchasePrice) {
        errors[FormField.PurchasePrice] = {
          type: 'required',
          message: requiredMsg,
        };
      }
      if (sellAsOffer && !offer) {
        errors[FormField.Offer] = {
          type: 'required',
          message: requiredMsg,
        };
      }
      if (sellAsOffer && !duration) {
        errors[FormField.Duration] = {
          type: 'required',
          message: requiredMsg,
        };
      }

      if (
        sellAsFixed &&
        sellAsOffer &&
        parseInt(offer, 10) >= parseInt(purchasePrice, 10)
      ) {
        errors[FormField.Offer] = {
          type: 'min',
          message: moreThanFixedMsg,
        };
      }

      return { values, errors };
    }) as Resolver<CreateAdoptableFormValues>,
  });
}

export function useEditForm(deviation: PapiDeviation) {
  const { t } = useTranslation();

  return useForm<{
    [FormField.SourceUrl]: string;
    [FormField.SourceFiles]: string;
  }>({
    // since we automatically uncheck the sell_as checkboxes when
    // its inputs are cleared, we use onSubmit here instead of onChange
    // or we get bad 'required' errors
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      [getSourceUrlInputName(deviation)]:
        deviation.extended?.adoptable?.assetSourceUrl ?? '',
      [getSourceFilesInputName(deviation)]:
        JSON.stringify(
          (deviation.extended?.adoptable?.assets ?? []).map(assetAdapter)
        ),
    },
    resolver: (async (data: EditAdoptableFormValues, context) => {
      const requiredMsg = t('sellerModal.error.required');
      const validUrl = t('sellerModal.create.source_url.invalidUrl');

      const { values, errors } = await yupResolver<EditAdoptableFormValues>(
        yup.object({
          [FormField.SourceUrl]: yup
            .string()
            .url(validUrl)
            .required(requiredMsg),
          [FormField.SourceFiles]: yup
            .string()
            .test(
              'at-least-one',
              requiredMsg,
              str => jsonArrayHasAtLeastOne(str)
            ),
        })
      )(data, context);

      return { values, errors };
    }) as Resolver<EditAdoptableFormValues>,
  });
}

export function useTurnOffSaleOptionCallback() {
  const { trigger, setValue, clearErrors, getValues } = useFormContext();
  return useCallback(
    (name: FormField.SellAsOffer | FormField.SellAsFixed) => {
      const values = getValues();

      // turning off an sale type checkbox, clears its inputs
      setValue(name, false);
      if (name === FormField.SellAsFixed) {
        setValue(FormField.PurchasePrice, '');
        clearErrors(FormField.PurchasePrice);
        trigger(FormField.PurchasePrice);
      } else if (name === FormField.SellAsOffer) {
        setValue(FormField.Offer, '');
        clearErrors(FormField.Offer);
        setValue(FormField.Duration, '');
        clearErrors(FormField.Duration);
        trigger(FormField.Offer);
      }

      // When both are toggle off switch the opposite one back on with defaults
      if (
        !values[FormField.SellAsFixed] &&
        !values[FormField.SellAsOffer] &&
        name === FormField.SellAsOffer
      ) {
        setValue(FormField.SellAsFixed, true);
        setValue(FormField.PurchasePrice, DEFAULT_VALUES.purchase_price);
        trigger(FormField.PurchasePrice);
      }

      if (
        !values[FormField.SellAsFixed] &&
        !values[FormField.SellAsOffer] &&
        name === FormField.SellAsFixed
      ) {
        setValue(FormField.SellAsOffer, true);
        setValue(FormField.Offer, DEFAULT_VALUES.offer);
        setValue(FormField.Duration, DEFAULT_VALUES.duration);
        trigger(FormField.Offer);
      }
    },
    [setValue, clearErrors, trigger]
  );
}
